import { useMemo } from "react";
import { formatEther } from "viem";
import { useAccount } from "wagmi";
import { PRIMARY_CHAIN_ID } from "@/constants/chain";
import {
  AQUABERA_HENLO_VAULT,
  FDV_CONTRACT_ADDRESS,
  HENLO_ADDRESS,
  HENLO_VAULT_ADDRESS,
} from "@/constants/contracts";
import { usePrice } from "@/hooks/haiku";
import { useReadErc20BalanceOf } from "@/lib/onchain/generated";

interface QuickStats {
  totalValueUSD: number;
  henloBalance: bigint;
  lockedBalance: bigint;
  vaultBalance: bigint;
  dailyChange: number;
  dailyChangePercent: number;
  unclaimedBGT: bigint;
  unclaimedOBERO: bigint;
  isLoading: boolean;
  error: Error | null;
}

export function useQuickStats(): QuickStats {
  const { address } = useAccount();
  const { data: priceData } = usePrice({ symbol: "HENLO" });
  const henloPrice = priceData?.usd || 0;
  const priceChange24h = priceData?.usd_24h_change || 0;
  const { data: henloBalance = 0n, isLoading: henloLoading } = useReadErc20BalanceOf({
    address: HENLO_ADDRESS as `0x${string}`,
    args: address ? [address] : undefined,
    chainId: PRIMARY_CHAIN_ID,
    query: { enabled: !!address },
  });
  const { data: lockedBalance = 0n, isLoading: lockedLoading } = useReadErc20BalanceOf({
    address: FDV_CONTRACT_ADDRESS as `0x${string}`,
    args: address ? [address] : undefined,
    chainId: PRIMARY_CHAIN_ID,
    query: { enabled: !!address },
  });
  const { data: vaultBalance1 = 0n } = useReadErc20BalanceOf({
    address: HENLO_VAULT_ADDRESS as `0x${string}`,
    args: address ? [address] : undefined,
    chainId: PRIMARY_CHAIN_ID,
    query: { enabled: !!address },
  });
  const { data: vaultBalance2 = 0n } = useReadErc20BalanceOf({
    address: AQUABERA_HENLO_VAULT as `0x${string}`,
    args: address ? [address] : undefined,
    chainId: PRIMARY_CHAIN_ID,
    query: { enabled: !!address },
  });
  const vaultBalance = vaultBalance1 + vaultBalance2;
  const unclaimedBGT = 0n;
  const unclaimedOBERO = 0n;
  const totalValueUSD = useMemo(() => {
    if (!henloPrice) return 0;
    const totalHenlo = henloBalance + lockedBalance + vaultBalance;
    const totalHenloInEther = parseFloat(formatEther(totalHenlo));
    return totalHenloInEther * henloPrice;
  }, [henloBalance, lockedBalance, vaultBalance, henloPrice]);
  const dailyChange = useMemo(() => {
    if (!totalValueUSD || !priceChange24h) return 0;
    return totalValueUSD * (priceChange24h / 100);
  }, [totalValueUSD, priceChange24h]);
  const isLoading = henloLoading || lockedLoading;
  return {
    totalValueUSD,
    henloBalance,
    lockedBalance,
    vaultBalance,
    dailyChange,
    dailyChangePercent: priceChange24h,
    unclaimedBGT,
    unclaimedOBERO,
    isLoading,
    error: null,
  };
}

export function useFormattedQuickStats() {
  const stats = useQuickStats();
  const formatNumber = (value: number, decimals = 2): string => {
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(decimals)}M`;
    if (value >= 1_000) return `${(value / 1_000).toFixed(decimals)}K`;
    return value.toFixed(decimals);
  };
  const formatToken = (value: bigint): string => {
    const inEther = parseFloat(formatEther(value));
    return formatNumber(inEther, 1);
  };
  return {
    ...stats,
    formatted: {
      totalValueUSD: `$${formatNumber(stats.totalValueUSD)}`,
      henloBalance: formatToken(stats.henloBalance),
      lockedBalance: formatToken(stats.lockedBalance),
      vaultBalance: formatToken(stats.vaultBalance),
      dailyChange: `${stats.dailyChangePercent >= 0 ? "+" : ""}${stats.dailyChangePercent.toFixed(2)}%`,
      dailyChangeUSD: `${stats.dailyChange >= 0 ? "+" : "-"}$${Math.abs(stats.dailyChange).toFixed(2)}`,
    },
  };
}
